import * as portals from "react-reverse-portal";

import { IS_INTERNAL_ENV } from "constants/app.constants";
import { EvaChart } from "constants/charts.enums";
import {
  BASE_CHART_SETTINGS,
  BASE_MIDSTREAM_CHART_SETTINGS
} from "entities/charts/eva/settings";

import { DashboardCapabilities, GridWidgetTemplate } from "models/dashboard";

export const WidgetKeys = {
  ChartOldWidgetKey: "chart-old",
  ChartWidgetKey: "chart",
  CompletionWidgetKey: "completion",
  DataTableWidgetKey: "data-table",
  ExploitableReportWidgetKey: "exploitable-report",
  FacilityTicketWidgetKey: "facility-ticket",
  FeatureImportanceWidgetKey: "feature-importance",
  ForecastingWidgetKey: "forecasting",
  GeoMapLoaderWidgetKey: "geo-map",
  InfoWidgetKey: "info",
  MapWidgetKey: "map",
  MidstreamChartOldWidgetKey: "midstream-chart-old",
  MidstreamChartWidgetKey: "midstream-chart",
  MidstreamDataTableWidgetKey: "midstream-data-table",
  MultiphaseChartWidgetKey: "multiphase-chart",
  PadScenarioWidgetKey: "pad-scenario",
  SamRfPvWidgetKey: "sam-rf-pv",
  SamTableWidgetKey: "sam-table",
  SamWidgetKey: "sam",
  SurveyVisWidgetKey: "survey-vis",
  ThreeDWidgetKey: "threed",
  TypeLogWidgetKey: "type-log",
  TypeWellDetailsWidgetKey: "typewell-details-widget",
  TypeWellEditorWidgetKey: "typewell-editor-widget",
  WellCardWidgetKey: "well-card",
  WellDataAnalyzer: "well-data-analyzer",
  XdaViewerWidgetKey: "xdaviewer",
  XdaWidgetKey: "xda",
  AutomationWidgetKey: "automation",
  MidstreamNavigatorWidgetKey: "midstream-navigator",
  GeoMapDataTableWidgetKey: "geo-map-data-table",
  RigFracChartWidgetKey: "rigfrac-chart",
  RigFracDataTableWidgetKey: "rigfrac-data-table",
  WellStatusChartWidgetKey: "wellstatus-chart"
};

// Export individual keys for convenience
export const {
  ChartOldWidgetKey,
  ChartWidgetKey,
  CompletionWidgetKey,
  DataTableWidgetKey,
  ExploitableReportWidgetKey,
  FacilityTicketWidgetKey,
  FeatureImportanceWidgetKey,
  ForecastingWidgetKey,
  GeoMapLoaderWidgetKey,
  InfoWidgetKey,
  MapWidgetKey,
  MidstreamChartOldWidgetKey,
  MidstreamChartWidgetKey,
  MidstreamDataTableWidgetKey,
  MultiphaseChartWidgetKey,
  PadScenarioWidgetKey,
  SamRfPvWidgetKey,
  SamTableWidgetKey,
  SamWidgetKey,
  SurveyVisWidgetKey,
  ThreeDWidgetKey,
  TypeLogWidgetKey,
  TypeWellDetailsWidgetKey,
  TypeWellEditorWidgetKey,
  WellCardWidgetKey,
  WellDataAnalyzer,
  XdaViewerWidgetKey,
  XdaWidgetKey,
  AutomationWidgetKey,
  MidstreamNavigatorWidgetKey,
  GeoMapDataTableWidgetKey,
  RigFracChartWidgetKey,
  RigFracDataTableWidgetKey,
  WellStatusChartWidgetKey
} = WidgetKeys;

export const WidgetTypes: GridWidgetTemplate[] = [
  {
    component: "map",
    maxExpandW: 6,
    componentState: {},
    componentOptions: {},
    available: (capabilities?: DashboardCapabilities): boolean => {
      return !capabilities || !capabilities.hasMapWidget;
    }
  },
  {
    component: ChartWidgetKey,
    maxExpandW: 6,
    maxExpandH: 6,
    componentState: BASE_CHART_SETTINGS,
    componentOptions: {
      portalNode: portals.createHtmlPortalNode()
    }
  },
  {
    component: MidstreamChartWidgetKey,
    maxExpandW: 6,
    maxExpandH: 6,
    componentState: BASE_MIDSTREAM_CHART_SETTINGS,
    componentOptions: {
      portalNode: portals.createHtmlPortalNode()
    }
  },
  {
    component: "data-table",
    componentState: {},
    componentOptions: {}
  },
  {
    component: MidstreamDataTableWidgetKey,
    componentState: {},
    componentOptions: {}
    // Midstream base is free for all orgs (product 2026-08-07); only the
    // allocation-model extras (navigator, CO2, Well Contribution, map
    // interactions) sit behind the "Midstream Allocation Model" flag.
  },
  {
    component: "xda",
    maxExpandW: 6,
    maxExpandH: 6,
    componentState: {},
    componentOptions: {},
    available: (capabilities?: DashboardCapabilities): boolean => {
      return !capabilities || capabilities.hasMapWidget;
    }
  },
  {
    component: "threed",
    maxExpandW: 6,
    maxExpandH: 6,
    componentState: {},
    componentOptions: {},
    available: (capabilities?: DashboardCapabilities): boolean => {
      return !capabilities || capabilities.hasMapWidget;
    }
  },
  {
    component: "well-card",
    maxExpandW: 6,
    maxExpandH: 6,
    componentState: {},
    componentOptions: {},
    available: (capabilities?: DashboardCapabilities): boolean => {
      return !capabilities || capabilities.hasMapWidget;
    }
  },
  {
    component: "type-log",
    maxExpandW: 6,
    maxExpandH: 6,
    componentState: {},
    componentOptions: {},
    available: (capabilities?: DashboardCapabilities): boolean => {
      return !capabilities || capabilities.hasMapWidget;
    }
  },
  {
    component: "facility-ticket",
    maxExpandW: 6,
    maxExpandH: 6,
    componentState: {},
    componentOptions: {}
  },
  {
    component: "info",
    maxExpandW: 1,
    maxExpandH: 1,
    componentState: {
      AggregateField: "Header.EntityName",
      IsForecastToggleOn: false,
      SelectedForecastFolder: ""
    },
    componentOptions: {},
    available: (capabilities?: DashboardCapabilities): boolean => {
      return !capabilities || capabilities.hasMapWidget;
    }
  },
  {
    component: "survey-vis",
    maxExpandW: 6,
    maxExpandH: 6,
    componentState: {},
    componentOptions: {},
    available: (capabilities?: DashboardCapabilities): boolean => {
      return !capabilities || capabilities.hasMapWidget;
    }
  },
  {
    component: "completion",
    maxExpandW: 6,
    maxExpandH: 6,
    componentState: {},
    componentOptions: {},
    available: (capabilities?: DashboardCapabilities): boolean => {
      return !capabilities || capabilities.hasMapWidget;
    }
  },
  {
    component: "sam",
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {},
    // EVA-10: env gate replaced by the "SAM Charts" feature flag
    // (declarative gating via systemDashboardDB widget config); the
    // code-side gate below is a deliberate belt-and-braces duplicate of the
    // declarative widget row, so SAM stays hidden regardless of
    // deploy/seed order. Falls back to available (true) if hasFeature is
    // genuinely absent, matching FEATURE_FLAGS's default-enabled semantics.
    available: (_capabilities?: DashboardCapabilities, hasFeature?): boolean => {
      return hasFeature ? hasFeature("SAM Charts") : true;
    }
  },
  {
    component: "sam-table",
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {},
    // EVA-10: env gate replaced by the "SAM Charts" feature flag
    // (declarative gating via systemDashboardDB widget config); the
    // code-side gate below is a deliberate belt-and-braces duplicate of the
    // declarative widget row, so SAM stays hidden regardless of
    // deploy/seed order. Falls back to available (true) if hasFeature is
    // genuinely absent, matching FEATURE_FLAGS's default-enabled semantics.
    available: (_capabilities?: DashboardCapabilities, hasFeature?): boolean => {
      return hasFeature ? hasFeature("SAM Charts") : true;
    }
  },
  {
    component: "sam-rf-pv",
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {},
    // EVA-10: env gate replaced by the "SAM Charts" feature flag
    // (declarative gating via systemDashboardDB widget config); the
    // code-side gate below is a deliberate belt-and-braces duplicate of the
    // declarative widget row, so SAM stays hidden regardless of
    // deploy/seed order. Falls back to available (true) if hasFeature is
    // genuinely absent, matching FEATURE_FLAGS's default-enabled semantics.
    available: (_capabilities?: DashboardCapabilities, hasFeature?): boolean => {
      return hasFeature ? hasFeature("SAM Charts") : true;
    }
  },
  {
    component: "multiphase-chart",
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {}
  },
  {
    component: "exploitable-report",
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {},
    available: (): boolean => IS_INTERNAL_ENV
  },
  {
    component: TypeWellEditorWidgetKey,
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {
      portalNode: portals.createHtmlPortalNode()
    },
    available: (capabilities?: DashboardCapabilities): boolean => {
      return !capabilities || !capabilities.hasTypeWellEditorWidget;
    }
  },
  {
    component: TypeWellDetailsWidgetKey,
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {
      portalNode: portals.createHtmlPortalNode()
    },
    available: (capabilities?: DashboardCapabilities): boolean => {
      return !capabilities || !capabilities.hasTypeWellDetailsWidget;
    }
  },
  {
    component: "geo-map",
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {},
    available: (_capabilities?: DashboardCapabilities, hasFeature?): boolean => {
      return hasFeature && hasFeature("Geo Model Loader");
    }
  },
  {
    component: WellDataAnalyzer,
    maxExpandW: 6,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {},
    available: (_capabilities?: DashboardCapabilities, hasFeature?): boolean => {
      return hasFeature && hasFeature("App Tools");
    }
  },
  {
    component: PadScenarioWidgetKey,
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {},
    available: (
      _capabilities?: DashboardCapabilities,
      hasFeature?,
      userFeatureFlags?
    ): boolean => {
      return (
        hasFeature &&
        // Enable for both read/write and readonly users
        (hasFeature("Pad Scenario") ||
          hasFeature("Pad Scenario Readonly") ||
          hasFeature("Stick Management")) &&
        (!hasFeature("User Feature Flags") ||
          (hasFeature("User Feature Flags") &&
            (userFeatureFlags?.PadScenario?.enabled ?? false)))
      );
    }
  },
  {
    component: ForecastingWidgetKey,
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {},
    available: (_capabilities?: DashboardCapabilities, hasFeature?): boolean => {
      return hasFeature && hasFeature("Forecasting");
    }
  },
  {
    component: FeatureImportanceWidgetKey,
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {}
  },

  {
    component: AutomationWidgetKey,
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {},
    available: (_capabilities?: DashboardCapabilities, hasFeature?): boolean => {
      return hasFeature && hasFeature("Automation");
    }
  },
  {
    component: MidstreamNavigatorWidgetKey,
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {}
  },
  {
    component: GeoMapDataTableWidgetKey,
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {},
    available: (_capabilities?: DashboardCapabilities, hasFeature?): boolean => {
      return (
        hasFeature &&
        (hasFeature("Geo Model Loader") || hasFeature("Client Geo Upload Modal"))
      );
    }
  },
  {
    // Rig & Frac Chart widget (EVA-76) — gated by the "Rig And Frac Layers" beta feature.
    component: RigFracChartWidgetKey,
    maxExpandW: 6,
    maxExpandH: 6,
    componentState: {
      ...BASE_CHART_SETTINGS,
      chartType: EvaChart.RigFracActivity
    },
    componentOptions: {
      portalNode: portals.createHtmlPortalNode()
    },
    available: (_capabilities?: DashboardCapabilities, hasFeature?): boolean => {
      return !!hasFeature?.("Rig And Frac Layers");
    }
  },
  {
    component: RigFracDataTableWidgetKey,
    maxExpandW: 12,
    maxExpandH: 12,
    componentState: {},
    componentOptions: {},
    // EVA-66: frontend-only gating for v1 (GATE-DECISION-6) — reuses the
    // exact EVA-65 beta flag ("Rig And Frac Layers") that gates the
    // RIG_LAYER/FRAC_LAYER map rendering, so the widget only shows up for
    // orgs that already see rig/frac on the map.
    available: (_capabilities?: DashboardCapabilities, hasFeature?): boolean => {
      return Boolean(hasFeature && hasFeature("Rig And Frac Layers"));
    }
  },
  {
    // Well Status widget. No `available` predicate: the ticket's acceptance
    // criteria ask for a new widget with no feature gating, unlike EVA-76's
    // rig/frac widget (which is gated because it surfaces the rig/frac map
    // layers). Omitting the predicate makes it available to every org, the same
    // as the standard Chart widget.
    component: WellStatusChartWidgetKey,
    maxExpandW: 6,
    maxExpandH: 6,
    componentState: {
      ...BASE_CHART_SETTINGS,
      chartType: EvaChart.WellStatusOverTime
    },
    componentOptions: {
      portalNode: portals.createHtmlPortalNode()
    }
  }
];
